// @ts-nocheck
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, Search, UserPlus, Upload, FileText, Image as ImageIcon, X, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useDebounce } from '@/hooks/useDebounce';
import { usStates } from '@/lib/constants';
import PositionSelect from '@/components/rosters/PositionSelect';
import { cn } from '@/lib/utils';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface Props {
  eventRegistrationId: string;
  eventId?: string;
  teamId: string;
  teamName: string;
  onClose: () => void;
  onRosterChanged: () => void;
  isGuest?: boolean;
  defaultTab?: 'search' | 'manual' | 'import';
}

const UPLOAD_BUCKET = 'roster-uploads';

// ── helpers ───────────────────────────────────────────────────────────
async function addPlayer(payload: any) {
  const { data, error } = await invokeEdgeFunction('rle-add-player-to-roster', { body: payload });
  if (error) throw new Error(error.message || 'Request failed');
  if (data?.ok === false) throw new Error(data?.error?.message || 'Failed to add player');
  return data?.data ?? data;
}

function fieldVal(f: any): string {
  if (f == null) return '';
  if (typeof f === 'object' && 'value' in f) return f.value == null ? '' : String(f.value);
  return String(f);
}

// ── Search tab ────────────────────────────────────────────────────────
function SearchTab({ eventRegistrationId, teamId, onClose, onRosterChanged }: any) {
  const [query, setQuery] = useState('');
  const debounced = useDebounce(query.trim(), 300);
  const [matches, setMatches] = useState<any[]>([]);
  const [rlMatches, setRlMatches] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [addingId, setAddingId] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    if (debounced.length < 2) {
      setMatches([]);
      setRlMatches([]);
      setSearched(false);
      return;
    }
    setLoading(true);
    (async () => {
      try {
        const { data, error } = await invokeEdgeFunction('rle-search-players', {
          body: { query: debounced, team_context_id: teamId },
        });
        if (error) throw new Error(error.message);
        if (data?.ok === false) throw new Error(data?.error?.message || 'Search failed');
        const payload = data?.data ?? data ?? {};
        if (active) {
          setMatches(payload.matches ?? []);
          setRlMatches(payload.recruitlook_matches ?? []);
          setSearched(true);
        }
      } catch (e: any) {
        if (active) {
          toast.error(e.message || 'Search failed');
          setMatches([]);
          setRlMatches([]);
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [debounced, teamId]);

  const afterAdd = () => {
    setQuery('');
    setMatches([]);
    setRlMatches([]);
    setSearched(false);
    onRosterChanged();
    onClose?.();
  };

  const placementToast = (data: any) => {
    if (data?.is_guest) {
      toast.success('Added as a guest for this event (player is on another team\u2019s roster).');
    } else {
      toast.success('Added to roster.');
    }
  };

  const handleAddExisting = async (player: any, id: string) => {
    setAddingId(id);
    try {
      const data = await addPlayer({ event_registration_id: eventRegistrationId, action: 'link_existing', player_id: player.player_id ?? player.rle_player_id });
      placementToast(data);
      afterAdd();
    } catch (e: any) {
      toast.error(e.message || 'Failed to add player');
    } finally {
      setAddingId(null);
    }
  };

  const handleAddRecruitlook = async (rl: any) => {
    const id = `rl-${rl.recruitlook_profile_id}`;
    setAddingId(id);
    try {
      let data: any;
      if (rl.already_in_rle && rl.rle_player_id) {
        data = await addPlayer({ event_registration_id: eventRegistrationId, action: 'link_existing', player_id: rl.rle_player_id });
      } else {
        data = await addPlayer({
          event_registration_id: eventRegistrationId,
          action: 'link_recruitlook',
          recruitlook_profile_id: rl.recruitlook_profile_id,
          name: rl.name,
          grad_year: rl.grad_year,
          home_state: rl.state,
        });
      }
      placementToast(data);
      afterAdd();
    } catch (e: any) {
      toast.error(e.message || 'Failed to add player');
    } finally {
      setAddingId(null);
    }
  };

  const rleName = (m: any) =>
    `${m.canonical_first_name ?? ''} ${m.canonical_last_name ?? ''}`.trim() || m.name || 'Unknown';

  const hasResults = matches.length > 0 || rlMatches.length > 0;

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          className="pl-9"
          placeholder="Search players by name"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {loading && (
        <div className="space-y-2">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      )}

      {!loading && searched && !hasResults && (
        <p className="text-sm text-muted-foreground py-2 text-center">
          No matches — try "Add manually" to create a new player.
        </p>
      )}

      {!loading && matches.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">On RLEvents</p>
          {matches.map((m: any) => {
            const id = `rle-${m.player_id}`;
            const onRoster = !!m.already_on_roster;
            return (
              <div key={id} className={cn('flex items-center justify-between gap-3 border rounded-md p-3', onRoster && 'opacity-60')}>
                <div className="min-w-0">
                  <p className="font-medium truncate">{rleName(m)}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {m.grad_year && <Badge variant="outline">Grad {m.grad_year}</Badge>}
                    {m.home_state && <Badge variant="outline">{m.home_state}</Badge>}
                  </div>
                </div>
                {onRoster ? (
                  <Badge variant="secondary" className="shrink-0">Already on roster</Badge>
                ) : (
                  <Button size="sm" onClick={() => handleAddExisting(m, id)} disabled={addingId === id}>
                    {addingId === id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add to roster'}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!loading && rlMatches.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">From RecruitLook</p>
          {rlMatches.map((rl: any) => {
            const id = `rl-${rl.recruitlook_profile_id}`;
            const locParts = [rl.state, rl.city].filter(Boolean);
            const onRoster = !!rl.already_on_roster;
            return (
              <div key={id} className={cn('flex items-center justify-between gap-3 border rounded-md p-3', onRoster && 'opacity-60')}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium truncate">{rl.name}</p>
                    <Badge variant="secondary">RecruitLook</Badge>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {rl.grad_year && <Badge variant="outline">Grad {rl.grad_year}</Badge>}
                    {locParts.length > 0 && <Badge variant="outline">{locParts.join(', ')}</Badge>}
                    {rl.already_in_rle && (
                      <span className="text-xs text-muted-foreground self-center">Already on RLEvents</span>
                    )}
                  </div>
                </div>
                {onRoster ? (
                  <Badge variant="secondary" className="shrink-0">Already on roster</Badge>
                ) : (
                  <Button size="sm" onClick={() => handleAddRecruitlook(rl)} disabled={addingId === id}>
                    {addingId === id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add to roster'}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Can't find them? Switch to "Add manually" to create a new player.
      </p>
    </div>
  );
}

// ── Manual tab ────────────────────────────────────────────────────────
const EMPTY_MANUAL = {
  full_name: '',
  jersey_number: '',
  position: '',
  grad_year: '',
  email: '',
  phone: '',
  home_state: '',
};

const GRAD_YEAR_OPTIONS = ['2026', '2027', '2028', '2029', '2030', '2031', '2032'];

function ManualTab({ eventRegistrationId, teamId, onRosterChanged, isGuest }: any) {
  const [form, setForm] = useState({ ...EMPTY_MANUAL });
  const [submitting, setSubmitting] = useState(false);
  const set = (k: string) => (e: any) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.full_name.trim()) {
      toast.error('Full name is required.');
      return;
    }
    setSubmitting(true);
    try {
      const payload: any = {
        event_registration_id: eventRegistrationId,
        action: 'create_new',
        full_name: form.full_name.trim(),
      };
      if (form.jersey_number) payload.jersey_number = form.jersey_number;
      if (form.position) payload.position = form.position;
      if (form.grad_year) payload.grad_year = parseInt(form.grad_year) || null;
      if (form.email) payload.email = form.email.trim();
      if (form.phone) payload.phone = form.phone.trim();
      if (form.home_state) payload.home_state = form.home_state.trim();

      const data = await addPlayer(payload);
      if (data?.claim_invite_sent && form.email) {
        toast.success(`Player added — invite sent to ${form.email}`);
      } else if (data?.is_guest) {
        toast.success('Added as a guest for this event (player is on another team\u2019s roster).');
      } else {
        toast.success('Added to roster.');
      }
      setForm({ ...EMPTY_MANUAL });
      onRosterChanged();
    } catch (err: any) {
      toast.error(err.message || 'Failed to add player');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <Label>Full name *</Label>
          <Input value={form.full_name} onChange={set('full_name')} />
        </div>
        <div>
          <Label>Jersey number</Label>
          <Input value={form.jersey_number} onChange={set('jersey_number')} maxLength={3} />
        </div>
        <div>
          <Label>Position</Label>
          <PositionSelect teamId={teamId} value={form.position} onChange={(v) => setForm((f) => ({ ...f, position: v }))} />
        </div>
        <div>
          <Label>Grad year</Label>
          <Select
            value={form.grad_year}
            onValueChange={(v) => setForm((f) => ({ ...f, grad_year: v }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select grad year" />
            </SelectTrigger>
            <SelectContent>
              {GRAD_YEAR_OPTIONS.map((y) => (
                <SelectItem key={y} value={y}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Home state</Label>
          <Select
            value={form.home_state}
            onValueChange={(v) => setForm((f) => ({ ...f, home_state: v }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select state" />
            </SelectTrigger>
            <SelectContent>
              {usStates.map((s) => (
                <SelectItem key={s.abbreviation} value={s.abbreviation}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>Athlete email</Label>
          <Input type="email" value={form.email} onChange={set('email')} />
        </div>
        <div>
          <Label>Phone</Label>
          <Input formatPhone value={form.phone} onChange={set('phone')} />
        </div>
      </div>
      <p className="text-xs text-muted-foreground -mt-1">
        Add an email to automatically send the athlete an invite to claim their profile.
      </p>

      <div className="flex justify-end">
        <Button type="submit" disabled={submitting}>
          {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <UserPlus className="h-4 w-4 mr-1" />}
          Add player
        </Button>
      </div>
    </form>
  );
}

// ── Import tab ────────────────────────────────────────────────────────
function ImportTab({ eventRegistrationId, eventId, teamId, onRosterChanged }: any) {
  const { profile, user } = useAuth();
  const [mode, setMode] = useState<'paste' | 'file' | 'photo'>('paste');
  const [text, setText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [rows, setRows] = useState<any[] | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [committing, setCommitting] = useState(false);

  const handleParseResult = (data: any) => {
    const players = data?.players ?? data?.data?.players ?? [];
    if (!players.length) {
      toast.error('No players could be extracted. Check your input and try again.');
      return;
    }
    setWarnings(data?.warnings ?? data?.data?.warnings ?? []);
    setRows(
      players.map((p: any) => ({
        include: true,
        first_name: fieldVal(p.first_name),
        last_name: fieldVal(p.last_name),
        jersey_number: fieldVal(p.jersey_number),
        grad_year: fieldVal(p.grad_year),
        email: fieldVal(p.email) || fieldVal(p.parent_email),
        match_candidates: p.match_candidates ?? [],
        link_player_id: null as string | null,
      }))
    );
  };

  const invokeParse = async (body: any) => {
    setParsing(true);
    setRows(null);
    setWarnings([]);
    try {
      const { data, error } = await invokeEdgeFunction('parse-roster', {
        body: { ...body, event_id: eventId, team_id: teamId },
      });
      if (error) throw new Error(error.message);
      if (data?.ok === false) throw new Error(data?.error?.message || 'Parse failed');
      handleParseResult(data);
    } catch (e: any) {
      toast.error(e.message || 'Failed to parse roster');
    } finally {
      setParsing(false);
    }
  };

  const handlePaste = () => {
    if (text.trim().length < 3) {
      toast.error('Paste some roster text first.');
      return;
    }
    invokeParse({ input_type: 'text', content: text });
  };

  const handleUpload = async (file: File, kind: 'file' | 'image') => {
    setParsing(true);
    setRows(null);
    setWarnings([]);
    try {
      const uid = profile?.id ?? user?.id ?? 'anon';
      const safeName = file.name
        .replace(/[\s\u00A0\u202F]+/g, '-')
        .replace(/[^A-Za-z0-9._-]/g, '-')
        .replace(/-{2,}/g, '-')
        .replace(/^-+|-+$/g, '');
      const path = `${uid}/${Date.now()}-${safeName}`;
      const { error: upErr } = await supabase.storage.from(UPLOAD_BUCKET).upload(path, file);
      if (upErr) throw new Error(`Upload failed: ${upErr.message}`);
      const { data: urlData, error: urlErr } = await supabase.storage
        .from(UPLOAD_BUCKET)
        .createSignedUrl(path, 600);
      if (urlErr || !urlData?.signedUrl) throw new Error('Failed to create signed URL');
      if (kind === 'file') {
        await invokeParse({ input_type: 'file', file_url: urlData.signedUrl, mime_type: file.type });
      } else {
        await invokeParse({ input_type: 'image', image_url: urlData.signedUrl, mime_type: file.type });
      }
    } catch (e: any) {
      toast.error(e.message || 'Failed to upload');
      setParsing(false);
    }
  };

  const updateRow = (i: number, k: string, v: any) =>
    setRows((rs) => rs!.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  const selectedCount = rows?.filter((r) => r.include).length ?? 0;

  const handleCommit = async () => {
    const selected = rows!.filter((r) => r.include);
    if (!selected.length) {
      toast.error('Select at least one player.');
      return;
    }
    setCommitting(true);
    let added = 0;
    let invites = 0;
    let failed = 0;
    for (const r of selected) {
      try {
        let payload: any;
        if (r.link_player_id) {
          payload = { event_registration_id: eventRegistrationId, action: 'link_existing', player_id: r.link_player_id };
        } else {
          payload = {
            event_registration_id: eventRegistrationId,
            action: 'create_new',
            first_name: r.first_name.trim(),
            last_name: r.last_name.trim(),
          };
          if (r.jersey_number) payload.jersey_number = r.jersey_number;
          if (r.grad_year) payload.grad_year = parseInt(r.grad_year) || null;
          if (r.email) payload.email = r.email.trim();
        }
        const data = await addPlayer(payload);
        added++;
        if (data?.claim_invite_sent) invites++;
      } catch {
        failed++;
      }
    }
    setCommitting(false);
    let msg = `Added ${added} player${added === 1 ? '' : 's'}`;
    if (invites > 0) msg += `, ${invites} invite${invites === 1 ? '' : 's'} sent`;
    if (failed > 0) msg += ` (${failed} failed)`;
    toast.success(msg);
    setRows(null);
    setText('');
    onRosterChanged();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Button variant={mode === 'paste' ? 'default' : 'outline'} size="sm" onClick={() => setMode('paste')}>
          <FileText className="h-4 w-4 mr-1" /> Paste
        </Button>
        <Button variant={mode === 'file' ? 'default' : 'outline'} size="sm" onClick={() => setMode('file')}>
          <Upload className="h-4 w-4 mr-1" /> Upload file
        </Button>
        <Button variant={mode === 'photo' ? 'default' : 'outline'} size="sm" onClick={() => setMode('photo')}>
          <ImageIcon className="h-4 w-4 mr-1" /> Photo
        </Button>
      </div>

      {mode === 'paste' && (
        <div className="space-y-2">
          <Textarea
            rows={6}
            placeholder="Paste your roster (names, emails, jersey numbers — any format)"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <Button onClick={handlePaste} disabled={parsing}>
            {parsing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Parse roster
          </Button>
        </div>
      )}

      {mode === 'file' && (
        <div>
          <Label>Upload a CSV or Excel file</Label>
          <Input
            type="file"
            accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            disabled={parsing}
            onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0], 'file')}
          />
        </div>
      )}

      {mode === 'photo' && (
        <div>
          <Label>Upload a photo of your roster</Label>
          <Input
            type="file"
            accept="image/*"
            disabled={parsing}
            onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0], 'image')}
          />
        </div>
      )}

      {parsing && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Parsing roster with AI…
        </div>
      )}

      {warnings.length > 0 && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <ul className="list-disc pl-4">
              {warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {rows && rows.length > 0 && (
        <div className="space-y-3">
          <p className="text-sm font-medium">Review players ({rows.length})</p>
          <div className="border rounded-md overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>First</TableHead>
                  <TableHead>Last</TableHead>
                  <TableHead className="w-16">#</TableHead>
                  <TableHead className="w-20">Grad</TableHead>
                  <TableHead>Email</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r, i) => (
                  <>
                    <TableRow key={`r-${i}`}>
                      <TableCell>
                        <Checkbox checked={r.include} onCheckedChange={(v) => updateRow(i, 'include', !!v)} />
                      </TableCell>
                      <TableCell>
                        <Input value={r.first_name} onChange={(e) => updateRow(i, 'first_name', e.target.value)} className="h-8" />
                      </TableCell>
                      <TableCell>
                        <Input value={r.last_name} onChange={(e) => updateRow(i, 'last_name', e.target.value)} className="h-8" />
                      </TableCell>
                      <TableCell>
                        <Input value={r.jersey_number} onChange={(e) => updateRow(i, 'jersey_number', e.target.value)} className="h-8" />
                      </TableCell>
                      <TableCell>
                        <Input value={r.grad_year} onChange={(e) => updateRow(i, 'grad_year', e.target.value)} className="h-8" />
                      </TableCell>
                      <TableCell>
                        <Input value={r.email} onChange={(e) => updateRow(i, 'email', e.target.value)} className="h-8" />
                      </TableCell>
                    </TableRow>
                    {r.match_candidates?.length > 0 && (
                      <TableRow key={`m-${i}`}>
                        <TableCell></TableCell>
                        <TableCell colSpan={5}>
                          <div className="flex flex-wrap items-center gap-2 text-xs">
                            <span className="text-muted-foreground">Possible match:</span>
                            {r.match_candidates.map((c: any) => {
                              const cid = c.player_id ?? c.id;
                              const active = r.link_player_id === cid;
                              return (
                                <Button
                                  key={cid}
                                  size="sm"
                                  variant={active ? 'default' : 'outline'}
                                  className="h-6 px-2 text-xs"
                                  onClick={() => updateRow(i, 'link_player_id', active ? null : cid)}
                                >
                                  {c.display_name ?? `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim()}
                                  {c.grad_year ? ` (${c.grad_year})` : ''}
                                </Button>
                              );
                            })}
                            {r.link_player_id && (
                              <span className="text-muted-foreground">— will link instead of create</span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="flex justify-end">
            <Button onClick={handleCommit} disabled={committing || selectedCount === 0}>
              {committing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Add {selectedCount} player{selectedCount === 1 ? '' : 's'} to roster
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Panel ─────────────────────────────────────────────────────────────
export default function RosterAddPanel({
  eventRegistrationId,
  eventId,
  teamId,
  teamName,
  onClose,
  onRosterChanged,
  isGuest = false,
  defaultTab = 'search',
}: Props) {
  return (
    <Card className="border-primary/30">
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle>{isGuest ? `Add guest for this event` : `Add players to ${teamName}`}</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            {isGuest
              ? 'This player is added as a guest for this event only.'
              : 'Add players one at a time, or import a whole roster at once.'}
          </p>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent>
        <Alert className="mb-4 border-amber-500/50 bg-amber-50/60 dark:bg-amber-950/20 text-amber-900 dark:text-amber-100 [&>svg]:text-amber-600 dark:[&>svg]:text-amber-400">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <span className="font-semibold">Please do NOT enter the coach&apos;s email for a player.</span>{' '}
            Entering a coach email on a player record will cause the player to be deleted. Leaving the player email blank is completely fine. Adding the athlete&apos;s own email lets the player log in and complete the information required for the digital and printed college-coach packets distributed at the event.
          </AlertDescription>
        </Alert>
        <Tabs defaultValue={defaultTab}>
          <TabsList className="mb-4 flex-wrap h-auto">
            <TabsTrigger value="search">Search existing</TabsTrigger>
            <TabsTrigger value="manual">Add manually</TabsTrigger>
            <TabsTrigger value="import">Import (AI)</TabsTrigger>
          </TabsList>
          <TabsContent value="search">
            <SearchTab eventRegistrationId={eventRegistrationId} teamId={teamId} onClose={onClose} onRosterChanged={onRosterChanged} />
          </TabsContent>
          <TabsContent value="manual">
            <ManualTab eventRegistrationId={eventRegistrationId} teamId={teamId} onRosterChanged={onRosterChanged} isGuest={isGuest} />
          </TabsContent>
          <TabsContent value="import">
            <ImportTab
              eventRegistrationId={eventRegistrationId}
              eventId={eventId}
              teamId={teamId}
              onRosterChanged={onRosterChanged}
            />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
